import { useClickOutside, useComponentSize, useSafeTimeout } from '@mm/hooks';
import { ArrowStatefulIcon } from '@mm/icons';
import { colors } from '@mm/utils';
import classnames from 'classnames';
import React, { useCallback, useEffect, useState } from 'react';
import Popover from 'react-tiny-popover';
import './PrimarySelect.pcss';
import { PrimarySelectDropdown } from './PrimarySelectDropdown/PrimarySelectDropdown';
import { DownOutlined, UpOutlined } from '@ant-design/icons';

export type OrganizedDropdownOption = {
  title?: string;
  pageGroupId?: number;
  id: number;
  suggestions: DropdownOption[];
  [key: string]: any;
};

export type DropdownOption = {
  id: number | string;
  name?: string;
  [key: string]: any;
};

export type PrimarySelectOuterProps = {
  className?: string;
  dropdownClassName?: string;
  activeItemClassName?: string;
  activeItemNameClassName?: string;
  options?: DropdownOption[];
  onClickCapture?: (e: React.MouseEvent<HTMLDivElement>) => void;
  organizedOptions?: OrganizedDropdownOption[];
  value?: DropdownOption;
  onChange: (option: DropdownOption) => any;
  placeholder?: React.ReactNode;
  displayNameKey?: string;
  showSearchFilter?: boolean;
  showPlaceholderInDropdown?: boolean;
  customOptionRenderer?: (
    option: DropdownOption,
    activeItem?: boolean,
  ) => React.ReactNode | Element[] | JSX.Element;
  dropdownWidth?: 'auto' | number;
  disabled?: boolean;
  searchRightSide?: React.ReactElement;
  underSearchInsert?: React.ReactElement;
  customArrowIcon?: React.ReactNode;
  customDropdownStyles?: Partial<typeof dropdownStyles>;
  renderWithoutPopover?: boolean;
  customOptionsPosition?: 'top' | 'bottom';
  closeOnSearchRightSideClick?: boolean;
  shouldCloseDropdown?: boolean;
  isLoading?: boolean;
  onScrollEnd?: any;
  customDropdownAlign?: 'start' | 'center' | 'end';
  useAntIcons?: boolean;
};

const useDropdownState = (props: PrimarySelectOuterProps) => {
  const { disabled } = props;
  const [showDropdown, setShowDropdown] = useState(false);
  const [dropdownTransitionActive, setDropdownTransitionActive] =
    useState(false);
  const { safeTimeout } = useSafeTimeout();

  const setDropdownOpen = (newDropdownState: boolean) => {
    if (showDropdown !== newDropdownState) {
      if (newDropdownState) {
        setShowDropdown(true);
        safeTimeout(() => setDropdownTransitionActive(true), 50);
      } else {
        setDropdownTransitionActive(false);
        safeTimeout(() => setShowDropdown(false), 120);
      }
    }
  };

  return {
    dropdownOpen: !disabled && showDropdown,
    dropdownTransitionActive,
    setDropdownOpen,
  };
};

const dropdownStyles = {
  zIndex: '10000000', // overwrite slideout menus
  position: 'fixed',
  overflow: 'visible',
};

const PrimarySelect = (props: PrimarySelectOuterProps) => {
  const {
    className,
    options,
    onChange,
    value,
    placeholder,
    showPlaceholderInDropdown,
    showSearchFilter,
    dropdownClassName,
    activeItemClassName,
    activeItemNameClassName,
    organizedOptions,
    customOptionRenderer,
    dropdownWidth,
    onClickCapture,
    disabled,
    displayNameKey = 'name',
    searchRightSide,
    underSearchInsert,
    customArrowIcon,
    customDropdownStyles,
    renderWithoutPopover,
    customOptionsPosition,
    closeOnSearchRightSideClick,
    shouldCloseDropdown,
    isLoading,
    onScrollEnd,
    customDropdownAlign,
    useAntIcons,
  } = props;
  const { dropdownOpen, dropdownTransitionActive, setDropdownOpen } =
    useDropdownState(props);
  const clickOutsideRef = useClickOutside<HTMLDivElement>(() =>
    setDropdownOpen(false),
  );
  const arrowDirection = dropdownOpen ? 'up' : 'down';
  const selectedText = value ? value[displayNameKey] : placeholder;
  const classList = classnames('primary-select', className, {
    'primary-select--disabled': disabled,
  });

  const activeItemClass = classnames(
    'primary-select__active-item',
    activeItemClassName,
  );
  const activeItemNameClass = classnames(
    'primary-select__active-item-name',
    {
      'primary-select__active-item-name--value': value,
    },
    activeItemNameClassName,
  );
  const [ref, { width }] = useComponentSize<HTMLDivElement>();

  const visibleOptions: DropdownOption[] | undefined = showPlaceholderInDropdown
    ? [{ id: 0, [displayNameKey]: placeholder }, ...(options || [])]
    : options;

  const closeDropdown = useCallback(() => {
    if (dropdownOpen) {
      setDropdownOpen(false);
    }
  }, [dropdownOpen, setDropdownOpen]);

  const onOptionSelect = useCallback(
    (option: DropdownOption) => {
      onChange(option);
      closeDropdown();
    },
    [onChange, closeDropdown],
  );

  useEffect(() => {
    if (shouldCloseDropdown) {
      closeDropdown();
    }
  }, [shouldCloseDropdown, closeDropdown]);

  const itemBody = (
    <>
      <div
        className={activeItemClass}
        onClick={e => {
          if (disabled) {
            return;
          }
          setDropdownOpen(!dropdownOpen);
        }}
        onClickCapture={onClickCapture}
      >
        <div className={activeItemNameClass}>
          {customOptionRenderer && value
            ? customOptionRenderer(value, true)
            : selectedText}
        </div>

        {customArrowIcon ? (
          customArrowIcon
        ) : useAntIcons ? (
          dropdownOpen ? (
            <UpOutlined style={{ fontSize: '16px', color: '#262626' }} />
          ) : (
            <DownOutlined style={{ fontSize: '16px', color: '#262626' }} />
          )
        ) : (
          <ArrowStatefulIcon
            className="primary-select__arrow-icon"
            direction={arrowDirection}
            color={colors.palePurple}
            size={15}
          />
        )}
      </div>
    </>
  );

  const dropdownBody = (
    <PrimarySelectDropdown
      displayNameKey={displayNameKey}
      width={dropdownWidth || width}
      onOptionSelect={onOptionSelect}
      options={visibleOptions}
      organizedOptions={organizedOptions}
      selectedOption={value}
      showSearchFilter={showSearchFilter}
      dropdownTransitionActive={dropdownTransitionActive}
      className={dropdownClassName}
      customOptionRenderer={customOptionRenderer}
      searchRightSide={searchRightSide}
      underSearchInsert={underSearchInsert}
      closeDropdown={closeDropdown}
      closeOnSearchRightSideClick={closeOnSearchRightSideClick}
      isLoading={isLoading}
      onScrollEnd={onScrollEnd}
    />
  );

  const activeBody = !renderWithoutPopover ? (
    <Popover
      isOpen={dropdownOpen}
      onClickOutside={closeDropdown}
      align={customDropdownAlign || 'start'}
      position={customOptionsPosition || 'bottom'}
      containerStyle={{ ...dropdownStyles, ...(customDropdownStyles || {}) }}
      disableReposition={true}
      content={() => dropdownBody}
    >
      {itemBody}
    </Popover>
  ) : (
    <div ref={clickOutsideRef}>
      {itemBody}
      {dropdownOpen && (
        <div
          style={
            {
              position: 'absolute',
              ...(Number(customDropdownStyles?.zIndex)
                ? { zIndex: Number(customDropdownStyles?.zIndex) }
                : {}),
            } as const
          }
        >
          {dropdownBody}
        </div>
      )}
    </div>
  );

  return (
    <div className={classList} ref={ref}>
      {disabled ? itemBody : activeBody}
    </div>
  );
};

export default PrimarySelect;
